import {
  useNavigate,
  useParams,
  useRouterState,
  useSearch
} from '@tanstack/react-router'

import { useTenantOptional } from '@/shared/contexts/TenantContext'

import type { AppRouter, AppRouterQuery } from './useAppRouter.types'
import { parseAppRouterTarget } from './parseAppRouterTarget'

const isExternalNavigation = (url: string) =>
  url.startsWith('http://') ||
  url.startsWith('https://') ||
  url.startsWith('/api/')

export function useAppRouter(): AppRouter {
  const navigate = useNavigate()
  const params = useParams({ strict: false }) as AppRouterQuery
  const search = useSearch({ strict: false }) as AppRouterQuery
  const { pathname, searchStr } = useRouterState({
    select: state => ({
      pathname: state.location.pathname,
      searchStr: state.location.searchStr
    })
  })

  const tenant = useTenantOptional()
  const query: AppRouterQuery = {
    ...params,
    ...search,
    ...(tenant?.eventSlug
      ? {
          eventSlug: tenant.eventSlug,
          digitalProductId: tenant.digitalProductId
        }
      : {})
  }

  const navigateTo = (url: string, replace: boolean) => {
    if (isExternalNavigation(url)) {
      if (replace) window.location.replace(url)
      else window.location.assign(url)
      return
    }

    const { to, search } = parseAppRouterTarget(url)

    navigate({
      to,
      search: () => search,
      replace
    })
  }

  return {
    pathname,
    asPath: `${pathname}${searchStr}`,
    isReady: true,
    query,
    push: url => navigateTo(url, false),
    replace: url => navigateTo(url, true),
    back: () => window.history.back()
  }
}
